import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { LoginPage } from "./pages/LoginPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { DashboardLayout } from "./components/DashboardLayout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { useActivityTracker } from "./hooks/useActivityTracker";
import { EmailVerification } from "./components/EmailVerification";
import { PasswordVerificationPage } from "./pages/PasswordVerificationPage";

// Lazy-loaded dashboard pages — only fetched when navigated to
const LinksPage = React.lazy(() =>
  import("./pages/LinksPage").then((m) => ({ default: m.LinksPage })),
);
const QRCodesPage = React.lazy(() =>
  import("./pages/QRCodesPage").then((m) => ({ default: m.QRCodesPage })),
);
const AnalyticsPage = React.lazy(() => import("./pages/AnalyticsPage"));
const DomainsPage = React.lazy(() =>
  import("./pages/DomainsPage").then((m) => ({ default: m.DomainsPage })),
);
const SettingsPage = React.lazy(() =>
  import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
    </div>
  );
}

function AppContent() {
  // Automatically track user activity for session management
  useActivityTracker();

  return (
    <Routes>
      {/* Redirect root to dashboard */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<EmailVerification />} />
      <Route path="/verify/:slug" element={<PasswordVerificationPage />} />

      {/* Protected dashboard routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        {/* Default dashboard route - redirect to links */}
        <Route index element={<Navigate to="/dashboard/links" replace />} />

        {/* Dashboard sub-routes — lazy loaded */}
        <Route
          path="links"
          element={
            <Suspense fallback={<PageLoader />}>
              <LinksPage />
            </Suspense>
          }
        />
        <Route
          path="qr-codes"
          element={
            <Suspense fallback={<PageLoader />}>
              <QRCodesPage />
            </Suspense>
          }
        />
        <Route
          path="analytics"
          element={
            <Suspense fallback={<PageLoader />}>
              <AnalyticsPage />
            </Suspense>
          }
        />
        <Route
          path="domains"
          element={
            <Suspense fallback={<PageLoader />}>
              <DomainsPage />
            </Suspense>
          }
        />
        <Route
          path="settings"
          element={
            <Suspense fallback={<PageLoader />}>
              <SettingsPage />
            </Suspense>
          }
        />
      </Route>

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
